/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Subscription } from 'rxjs/internal/Subscription';

export interface ChipsElementModel {
  name: string;
  id: string;
  type?: string;
  url?: string;
  displayName?: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-chips-autocomplete',
  templateUrl: './chips-autocomplete.component.html',
  styleUrls: ['./chips-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => ChipsAutocompleteComponent)
    }
  ],
  standalone: false
})
export class ChipsAutocompleteComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  faXmark = faXmark;

  controlValueChangesSubscription!: Subscription;
  control: FormControl = new FormControl('');
  @Input() title!: string;
  @Input() inputPlaceholder!: string;
  @Input() allElements: ChipsElementModel[] = [];
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
  filteredElements!: ChipsElementModel[];
  selectedElements: ChipsElementModel[] = [];

  @ViewChild('elementsInput') elementsInput!: ElementRef<HTMLInputElement>;

  separatorKeysCodes: number[] = [ENTER, COMMA];
  private controlValue: string[] = [];
  private controlValueOnChange: (value: any) => void = () => {};
  private controlValueOnTouched: () => void = () => {};

  @Output() selectionchange = new EventEmitter<any[]>();
  @Input() value: string[] = [];

  constructor(private router: Router) {}

  ngAfterViewInit(): void {
    if (this.controlValue) {
      this.writeValue(this.controlValue);
    }
    if (this.controlValueOnChange) {
      this.registerOnChange(this.controlValueOnChange);
    }
    if (this.controlValueOnTouched) {
      this.registerOnTouched(this.controlValueOnTouched);
    }
  }

  writeValue(obj: any): void {
    this.controlValue = obj;
    this.updateChips();
  }

  registerOnChange(fn: any): void {
    this.controlValueOnChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.controlValueOnTouched = fn;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value) {
      this.writeValue(changes.value.currentValue);
    }
    if (
      changes.allElements &&
      !changes.allElements.firstChange &&
      changes.allElements.previousValue != changes.allElements.currentValue
    ) {
      this.allElements = changes.allElements.currentValue;
      this.controlValue = this.controlValue.filter((id) =>
        this.allElements.some((element) => element.id === id)
      );
      this.updateChips();
    }
  }

  ngOnDestroy(): void {
    this.controlValueChangesSubscription!.unsubscribe();
  }

  ngOnInit(): void {
    this.filteredElements = this.allElements;
    this.controlValueChangesSubscription = this.control.valueChanges.subscribe((value) => {
      let unselectedElements = this.allElements.filter(
        (item) => !this.controlValue?.includes(item.id)
      );
      this.filteredElements = value
        ? (this._filter(value, unselectedElements) ?? [])
        : unselectedElements;
    });
  }

  private _filter(value: string, elements: ChipsElementModel[]): ChipsElementModel[] | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const filterValue = value.toLowerCase();
    return elements.filter((element) => element.name.toLowerCase().includes(filterValue));
  }

  remove(element: ChipsElementModel): void {
    const index = this.controlValue.findIndex((item) => item === element.id);
    this.controlValue.splice(index, 1);
    this.updateChips();
    this.selectionchange.emit(
      this.selectedElements.map((element) => {
        return { id: element.id, type: element.type };
      })
    );
    this.controlValueOnChange(this.controlValue);
    this.controlValueOnTouched();
  }

  select(element: ChipsElementModel) {
    this.controlValue.push(element.id);
    this.updateChips();
    this.selectionchange.emit(
      this.selectedElements.map((element) => {
        return { id: element.id, type: element.type };
      })
    );
    this.controlValueOnChange(this.controlValue);
    this.controlValueOnTouched();
  }

  updateChips(): void {
    this.selectedElements = this.allElements.filter((item) => this.controlValue?.includes(item.id));
    if (this.elementsInput && this.elementsInput.nativeElement) {
      this.elementsInput.nativeElement.value = '';
    }
    this.control.setValue(null);
  }

  onChipClick(element: ChipsElementModel) {
    if (element.url) this.router.navigate([`${element.url}${element.id}`]);
  }
}
